import { AddressModalData } from '@/types/address/address.types';
import {
    getBidderMobileVerified,
    getDefaultAddressIsVerified,
    isProfileComplete,
    isUserAtLeastPartiallyRegistered,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import {
    getTriggerId,
    isCompleteAccountModalOpen,
    isTracking,
} from '@/redux/modules/account/shouldLogin/shouldLogin.selectors';
import { getUiUpdateAccount } from '@/redux/modules/account/updateAccount/updateAccount';
import { ModalContext } from '@/redux/modules/modal';
import {
    openCompleteAccountSignupModalFromChecker,
    stopTracker,
} from '@/redux/modules/account/shouldLogin/shouldLogin.actions';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'react-fast-compare';

type RequireLogInContainerProps<RunFunctionArgumentType = {}> = {
    allSearchParams?: SearchParamsType;
    completeAddressData?: AddressModalData;
    forceConfirmPassword?: boolean;
    forceSignUp?: boolean;
    modalContext?: ModalContext;
    requireCompleteProfile?: boolean;
    requireLogin?: boolean;
    requireUserAtLeastPartiallyRegistered?: boolean;
    requireVerifiedAddress?: boolean;
    requireVerifiedPhoneNumber?: boolean;
    runFunction?: (arg: RunFunctionArgumentType) => void;
    runFunctionArgument?: RunFunctionArgumentType | undefined;
};

export const useRequireLogin = () => {
    const [shouldRunCallback, setShouldRunCallback] = useState(false);
    const [loginRequirements, setLoginRequirements] = useState<{
        forceSignUp?: boolean;
        requireCompleteProfile?: boolean;
        requireLogin?: boolean;
        requireUserAtLeastPartiallyRegistered?: boolean;
        requireVerifiedAddress?: boolean;
        requireVerifiedPhoneNumber?: boolean;
    } | null>(null);
    const [storedRunFunction, setStoredRunFunction] = useState<(() => void) | null>(null);
    const guid = useMemo(() => uuidv4(), []);
    const dispatch = useAppDispatch();

    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isCompleteProfile = useAppSelector(isProfileComplete);
    const triggerId = useAppSelector(getTriggerId, isEqual);
    const tracking = useAppSelector(isTracking, isEqual);
    const completeAccountModalOpen = useAppSelector(isCompleteAccountModalOpen);
    const isMobileVerified = useAppSelector(getBidderMobileVerified);

    const userAtLeastPartiallyRegistered = useAppSelector(isUserAtLeastPartiallyRegistered);
    const uiUpdateAccount = useAppSelector(getUiUpdateAccount);

    const isDefaultAddressVerified = useAppSelector(getDefaultAddressIsVerified);
    useEffect(() => {
        if (guid === triggerId && tracking && loginRequirements) {
            if (
                !completeAccountModalOpen &&
                userAtLeastPartiallyRegistered &&
                (userLoggedIn || !loginRequirements?.requireLogin) &&
                (isCompleteProfile || !loginRequirements?.requireCompleteProfile) &&
                (isMobileVerified || !loginRequirements?.requireVerifiedPhoneNumber) &&
                (isDefaultAddressVerified || !loginRequirements?.requireVerifiedAddress)
            ) {
                setShouldRunCallback(true);
            }

            if (!completeAccountModalOpen && !userLoggedIn && loginRequirements.requireLogin) {
                setLoginRequirements(null);
                setShouldRunCallback(false);
            }
        }
    }, [
        completeAccountModalOpen,
        dispatch,
        guid,
        isCompleteProfile,
        tracking,
        triggerId,
        uiUpdateAccount.submitted,
        uiUpdateAccount.success,
        userAtLeastPartiallyRegistered,
        userLoggedIn,
        loginRequirements,
        isDefaultAddressVerified,
        isMobileVerified,
    ]);

    useEffect(() => {
        if (shouldRunCallback && storedRunFunction) {
            storedRunFunction();
            dispatch(stopTracker());
            setShouldRunCallback(false);
        }
    }, [
        userLoggedIn,
        isCompleteProfile,
        userAtLeastPartiallyRegistered,
        completeAccountModalOpen,
        storedRunFunction,
        dispatch,
        shouldRunCallback,
    ]);

    return useCallback(
        async ({
            forceSignUp = true,
            modalContext,
            requireCompleteProfile = false,
            requireLogin = true,
            requireUserAtLeastPartiallyRegistered,
            requireVerifiedAddress = false,
            requireVerifiedPhoneNumber = false,
            runFunction,
            runFunctionArgument,
        }: RequireLogInContainerProps): Promise<any> => {
            if (
                (forceSignUp && !userLoggedIn && !userAtLeastPartiallyRegistered) ||
                (forceSignUp && !userLoggedIn && requireLogin) ||
                (!userAtLeastPartiallyRegistered && requireUserAtLeastPartiallyRegistered) ||
                (!isCompleteProfile && requireCompleteProfile) ||
                (isCompleteProfile && requireVerifiedAddress && !isDefaultAddressVerified) ||
                (isCompleteProfile && requireVerifiedPhoneNumber && !isMobileVerified)
            ) {
                setLoginRequirements({
                    forceSignUp,
                    requireCompleteProfile,
                    requireLogin,
                    requireUserAtLeastPartiallyRegistered,
                    requireVerifiedAddress,
                    requireVerifiedPhoneNumber,
                });
                dispatch(openCompleteAccountSignupModalFromChecker({ modalContext, trigger: guid }));
                setStoredRunFunction(() => () => runFunction?.(runFunctionArgument));
            } else {
                dispatch(stopTracker());
                return Promise.resolve(runFunction?.(runFunctionArgument));
            }
        },
        [
            dispatch,
            guid,
            isCompleteProfile,
            isDefaultAddressVerified,
            isMobileVerified,
            userAtLeastPartiallyRegistered,
            userLoggedIn,
        ]
    );
};
